/**
 * Node visual unificado para rastreamento (GTM, Meta Pixel, GA4)
 * Exibe ícone, label e detalhe de configuração conforme tipo
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo node de rastreamento
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tags, Target, BarChart3 } from 'lucide-react';
import { FlowNodeData } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Retorna configuração visual por tipo de rastreamento
 */
const getTrackingConfig = (nodeData: FlowNodeData) => {
  switch (nodeData.type) {
    case 'google_tag_manager':
      return {
        icon: Tags,
        label: 'Google Tag Manager',
        color: 'blue',
        borderColor: 'border-blue-500',
        bgColor: 'bg-blue-500/10',
        textColor: 'text-blue-600',
        handleColor: '!bg-blue-500',
        detail: nodeData.config?.event_name
          ? `📊 ${nodeData.config.event_name}`
          : 'Evento não configurado',
      };
    case 'meta_pixel':
      return {
        icon: Target,
        label: 'Meta Pixel',
        color: 'indigo',
        borderColor: 'border-indigo-500',
        bgColor: 'bg-indigo-500/10',
        textColor: 'text-indigo-600',
        handleColor: '!bg-indigo-500',
        detail: nodeData.config?.event_type === 'Custom'
          ? `📊 ${nodeData.config?.custom_event_name || 'Custom'}`
          : `📊 ${nodeData.config?.event_type || 'Evento não configurado'}`,
      };
    case 'google_analytics':
      return {
        icon: BarChart3,
        label: 'Google Analytics',
        color: 'orange',
        borderColor: 'border-orange-500',
        bgColor: 'bg-orange-500/10',
        textColor: 'text-orange-600',
        handleColor: '!bg-orange-500',
        detail: nodeData.config?.event_name
          ? `📊 ${nodeData.config.event_name}`
          : 'Evento não configurado',
      };
    default:
      return {
        icon: BarChart3,
        label: 'Rastreamento',
        color: 'gray',
        borderColor: 'border-emerald-500',
        bgColor: 'bg-emerald-500/10',
        textColor: 'text-emerald-600',
        handleColor: '!bg-emerald-500',
        detail: '',
      };
  }
};

/**
 * Componente visual do node de rastreamento
 */
export function TrackingNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;

  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;

  const config = getTrackingConfig(nodeData);
  const Icon = config.icon;

  return (
    <div className={`bg-card rounded-lg shadow-lg border-2 ${config.borderColor} min-w-[200px] max-w-[240px]`}>
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />

      <Handle
        type="target"
        position={Position.Top}
        className={`${config.handleColor} !w-3 !h-3`}
      />

      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className={`${config.bgColor} ${config.textColor} p-2 rounded`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || config.label}
            </div>
          </div>
        </div>

        {config.detail && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
            {config.detail}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className={`${config.handleColor} !w-3 !h-3`}
      />
    </div>
  );
}
